import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { AppLayout } from "@/components/AppLayout";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { RichTextEditor } from "@/components/RichTextEditor";
import { ApercuEmail } from "@/components/ApercuEmail";
import { Mail, Send, CheckCircle2, XCircle, Users, Eye, ChevronDown, UserPlus, Plus, Check, X, Search } from "lucide-react";

const TYPE_LABELS = { PRATICIEN: "Praticiens", VISITEUR: "Visiteurs" };
const STATUT_LABELS = { ACTIF: "Actifs", PROSPECT: "Prospects", EXPIRE: "Expirés", SUSPENDU: "Suspendus" };
const VARIABLES = [
  ["{{civilite}}", "Civilité"], ["{{prenom}}", "Prénom"], ["{{nom}}", "Nom"],
  ["{{institut}}", "Institut"], ["{{statut}}", "Statut"], ["{{fin_adhesion}}", "Fin d'adhésion"],
];

const inputCls = "w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-cfhtb-light";

function CheckList({ titre, items, selected, onToggle, testPrefix }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-md border border-slate-200" data-testid={`${testPrefix}-dropdown`}>
      <button type="button" data-testid={`${testPrefix}-toggle`} onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-2 px-3 py-2.5 text-sm font-medium text-cfhtb-heading hover:bg-cfhtb-bg/50 transition-colors rounded-md">
        <span className="flex items-center gap-2">
          {titre}
          {selected.length > 0 && (
            <span className="text-xs font-semibold text-cfhtb-primary bg-cfhtb-primary/10 rounded-full px-2 py-0.5"
              data-testid={`${testPrefix}-count`}>
              {selected.length} sélectionné(s)
            </span>
          )}
        </span>
        <ChevronDown className={`h-4 w-4 text-cfhtb-text transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="border-t border-slate-100 px-2 pb-2 pt-1.5">
          {items.length === 0 ? (
            <p className="text-xs text-cfhtb-text/60 px-2 py-1.5">Aucun élément disponible.</p>
          ) : (
            <div className="max-h-48 overflow-y-auto space-y-0.5" data-testid={`${testPrefix}-list`}>
              {items.map((it) => (
                <label key={it.value} data-testid={`${testPrefix}-${it.value}`}
                  className={`flex items-center gap-2.5 rounded-md px-2.5 py-1.5 text-sm cursor-pointer transition-colors ${
                    selected.includes(it.value)
                      ? "bg-cfhtb-primary/5 text-cfhtb-heading"
                      : "text-cfhtb-text hover:bg-cfhtb-bg/60"}`}>
                  <input type="checkbox" checked={selected.includes(it.value)} onChange={() => onToggle(it.value)}
                    className="h-4 w-4 rounded border-slate-300 text-cfhtb-primary focus:ring-cfhtb-light" />
                  {it.color && <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ backgroundColor: it.color }} />}
                  <span className="truncate">{it.label}</span>
                </label>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function MessagerieCampagnes() {
  const { user } = useAuth();
  const [config, setConfig] = useState(null);
  const [tags, setTags] = useState([]);
  const [instituts, setInstituts] = useState([]);
  const [seminaires, setSeminaires] = useState([]);
  const [criteres, setCriteres] = useState({ types: [], statuts: [], institut_ids: [], tag_ids: [], seminar_ids: [] });
  const [preview, setPreview] = useState(null);
  const [exclus, setExclus] = useState([]);
  const [objet, setObjet] = useState("");
  const [contenu, setContenu] = useState("<p>{{civilite}} {{prenom}} {{nom}},</p><p></p>");
  const [campBusy, setCampBusy] = useState(false);
  const [campResult, setCampResult] = useState(null);
  const [campagnes, setCampagnes] = useState([]);
  const [testEmail, setTestEmail] = useState("");
  const [signatures, setSignatures] = useState([]);
  const [sigId, setSigId] = useState("defaut");
  const [manuelQuery, setManuelQuery] = useState("");
  const [manuelResults, setManuelResults] = useState([]);
  const [membresManuels, setMembresManuels] = useState([]);

  useEffect(() => {
    api("/admin/messagerie/config").then(setConfig).catch(() => {});
    api("/admin/tags").then(setTags).catch(() => {});
    api("/admin/instituts").then(setInstituts).catch(() => {});
    api("/admin/seminaires").then(setSeminaires).catch(() => {});
    api("/admin/messagerie/campagnes").then(setCampagnes).catch(() => {});
    api("/admin/messagerie/signatures").then((d) => setSignatures(d.items)).catch(() => {});
  }, []);

  useEffect(() => {
    if (user?.email && !testEmail) setTestEmail(user.email);
  }, [user]);

  const toggleCritere = (key, value) => {
    setCriteres((c) => ({
      ...c,
      [key]: c[key].includes(value) ? c[key].filter((x) => x !== value) : [...c[key], value],
    }));
    setPreview(null);
    setExclus([]);
  };

  const toggleExclu = (id) => {
    setExclus((x) => (x.includes(id) ? x.filter((e) => e !== id) : [...x, id]));
  };

  const rechercherMembres = async (q) => {
    setManuelQuery(q);
    if (q.trim().length < 2) { setManuelResults([]); return; }
    try {
      const r = await api(`/admin/membres?q=${encodeURIComponent(q.trim())}&page_size=8`);
      setManuelResults(r.items || []);
    } catch { setManuelResults([]); }
  };

  const ajouterMembre = (m) => {
    if (!membresManuels.some((x) => x.id === m.id)) {
      setMembresManuels((l) => [...l, {
        id: m.id, nom: `${m.prenom || ""} ${m.nom || ""}`.trim(), email: m.email_principal,
      }]);
      setPreview(null);
      setExclus([]);
    }
  };

  const retirerMembre = (id) => {
    setMembresManuels((l) => l.filter((x) => x.id !== id));
    setPreview(null);
    setExclus([]);
  };

  const nbCriteres = Object.values(criteres).reduce((n, arr) => n + arr.length, 0) + membresManuels.length;
  const nbRetenus = preview ? preview.total - exclus.length : 0;

  const runPreview = async () => {
    try {
      const res = await api("/admin/messagerie/segment/previsualiser", { method: "POST", body: { ...criteres, membre_ids: membresManuels.map((m) => m.id) } });
      setPreview(res);
      setExclus([]);
    } catch (e) {
      toast.error(e.message);
    }
  };

  const sendCampaign = async (test) => {
    if (!objet.trim()) { toast.error("Saisissez un objet"); return; }
    if (test) {
      if (!window.confirm(`Envoyer un email de test à ${testEmail} ?`)) return;
    } else if (!window.confirm(`Envoyer la campagne à ${nbRetenus} destinataire(s) ?${exclus.length ? `\n(${exclus.length} contact(s) retiré(s) de la liste)` : ""}`)) {
      return;
    }
    setCampBusy(true);
    setCampResult(null);
    try {
      const res = await api("/admin/messagerie/campagnes", {
        method: "POST",
        body: { objet, contenu_html: contenu, criteres: { ...criteres, membre_ids: membresManuels.map((m) => m.id), exclus }, test, test_email: test ? testEmail : null, signature_id: sigId === "defaut" ? null : sigId },
      });
      setCampResult(res);
      if (res.envoye) toast.success(`Test envoyé à ${res.destinataire}`);
      else if (res.mode === "reel") toast.success(`Campagne envoyée : ${res.envoyes}/${res.destinataires}`);
      api("/admin/messagerie/campagnes").then(setCampagnes).catch(() => {});
    } catch (e) {
      toast.error(e.message);
    } finally {
      setCampBusy(false);
    }
  };

  return (
    <AppLayout variant="admin">
      <div data-testid="messagerie-page" className="max-w-4xl space-y-6 animate-fade-in">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Mail className="h-7 w-7 text-cfhtb-primary" />
            <div>
              <h1 className="font-serif text-3xl font-bold text-cfhtb-heading tracking-tight">Messagerie</h1>
              <p className="text-sm text-cfhtb-text">
                Campagnes Brevo personnalisées. Paramétrage (expéditeur, signature) : Administration → Messagerie.
              </p>
            </div>
          </div>
          <Link to="/admin/messagerie/onboarding" data-testid="onboarding-link"
            className="inline-flex items-center gap-2 bg-white text-cfhtb-primary border border-cfhtb-primary rounded-md px-4 py-2 text-sm font-medium hover:bg-cfhtb-bg transition-colors">
            <UserPlus className="h-4 w-4" /> Onboarding (jamais connectés)
          </Link>
        </div>

        <div className="bg-white rounded-lg border border-cfhtb-light/30 shadow-sm p-6 space-y-5" data-testid="audience-builder">
          <div className="flex items-center gap-2">
            <Users className="h-5 w-5 text-cfhtb-primary" />
            <h2 className="font-serif text-lg font-semibold text-cfhtb-heading">Destinataires</h2>
            <span className="text-xs text-cfhtb-text/70">(un membre est inclus s'il remplit au moins un critère)</span>
          </div>
          <div className="space-y-4">
            <CheckList titre="Type de membre" testPrefix="segment-type"
              items={Object.entries(TYPE_LABELS).map(([v, l]) => ({ value: v, label: l }))}
              selected={criteres.types} onToggle={(v) => toggleCritere("types", v)} />
            <CheckList titre="Statut" testPrefix="segment-statut"
              items={Object.entries(STATUT_LABELS).map(([v, l]) => ({ value: v, label: l }))}
              selected={criteres.statuts} onToggle={(v) => toggleCritere("statuts", v)} />
            <CheckList titre="Institut" testPrefix="segment-institut"
              items={instituts.map((i) => ({ value: i.id, label: i.nom, color: "#409AC3" }))}
              selected={criteres.institut_ids} onToggle={(v) => toggleCritere("institut_ids", v)} />
            <CheckList titre="Tags" testPrefix="segment-tag"
              items={tags.map((t) => ({ value: t.id, label: t.nom, color: t.couleur }))}
              selected={criteres.tag_ids} onToggle={(v) => toggleCritere("tag_ids", v)} />
            <CheckList titre="Inscrits à un événement" testPrefix="segment-seminaire"
              items={seminaires.map((s) => ({ value: s.id, label: s.titre, color: "#1E8E5A" }))}
              selected={criteres.seminar_ids} onToggle={(v) => toggleCritere("seminar_ids", v)} />

            <div className="rounded-md border border-slate-200" data-testid="segment-manuel">
              <div className="px-3 py-2.5">
                <label className="flex items-center gap-2 text-sm font-medium text-cfhtb-heading mb-1.5">
                  <UserPlus className="h-4 w-4 text-cfhtb-primary" /> Choisir manuellement des membres
                </label>
                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-cfhtb-text/50" />
                  <input data-testid="manuel-search-input" value={manuelQuery}
                    onChange={(e) => rechercherMembres(e.target.value)}
                    placeholder="Rechercher par nom ou email…"
                    className={`${inputCls} pl-8`} />
                </div>
                {manuelQuery.trim().length >= 2 && (
                  <ul className="mt-2 max-h-48 overflow-y-auto rounded-md border border-slate-200 divide-y divide-slate-50" data-testid="manuel-results">
                    {manuelResults.length === 0 ? (
                      <li className="px-3 py-2 text-xs text-cfhtb-text/60" data-testid="manuel-results-empty">Aucun membre trouvé.</li>
                    ) : manuelResults.map((m) => {
                      const deja = membresManuels.some((x) => x.id === m.id);
                      return (
                        <li key={m.id}>
                          <button type="button" data-testid={`manuel-add-${m.id}`} disabled={deja}
                            onClick={() => ajouterMembre(m)}
                            className="w-full flex items-center justify-between gap-2 px-3 py-1.5 text-sm text-left hover:bg-cfhtb-bg/60 disabled:opacity-50 disabled:cursor-default transition-colors">
                            <span className="truncate">
                              <span className="text-cfhtb-heading">{`${m.prenom || ""} ${m.nom || ""}`.trim() || "—"}</span>
                              <span className="text-cfhtb-text/70"> ({m.email_principal})</span>
                            </span>
                            {deja ? <Check className="h-4 w-4 text-cfhtb-success shrink-0" /> : <Plus className="h-4 w-4 text-cfhtb-primary shrink-0" />}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
                {membresManuels.length > 0 && (
                  <div className="mt-2.5 flex flex-wrap gap-2" data-testid="manuel-selected">
                    {membresManuels.map((m) => (
                      <span key={m.id} data-testid={`manuel-chip-${m.id}`}
                        className="inline-flex items-center gap-1.5 rounded-full bg-cfhtb-primary/10 text-cfhtb-primary text-xs font-medium px-2.5 py-1">
                        {m.nom || m.email}
                        <button type="button" data-testid={`manuel-remove-${m.id}`} onClick={() => retirerMembre(m.id)}
                          className="hover:text-cfhtb-secondary transition-colors">
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-4 pt-1">
            <button data-testid="segment-preview-button" onClick={runPreview} disabled={nbCriteres === 0}
              className="inline-flex items-center gap-2 bg-white text-cfhtb-primary border border-cfhtb-primary rounded-md px-4 py-2 text-sm font-medium hover:bg-cfhtb-bg disabled:opacity-50 transition-colors">
              <Eye className="h-4 w-4" /> Prévisualiser le segment
            </button>
          </div>

          {preview && (
            <div className="border border-cfhtb-light/40 rounded-md" data-testid="segment-preview-result">
              <div className="px-4 py-2.5 bg-cfhtb-bg/50 border-b border-cfhtb-light/20 text-sm">
                <span className="font-semibold text-cfhtb-primary" data-testid="segment-total">
                  {nbRetenus} destinataire(s) retenu(s)
                </span>
                <span className="text-xs text-cfhtb-text ml-2">
                  sur {preview.total} dans le segment{exclus.length > 0 ? ` — ${exclus.length} retiré(s)` : ""} · décochez un contact pour le retirer de l'envoi
                </span>
              </div>
              <ul className="max-h-56 overflow-y-auto divide-y divide-slate-50" data-testid="segment-destinataires">
                {preview.destinataires.map((d) => (
                  <li key={d.id} className="px-4 py-1.5">
                    <label className="flex items-center gap-2.5 text-sm cursor-pointer" data-testid={`dest-${d.id}`}>
                      <input type="checkbox" checked={!exclus.includes(d.id)} onChange={() => toggleExclu(d.id)}
                        className="h-4 w-4 rounded border-slate-300 text-cfhtb-primary focus:ring-cfhtb-light" />
                      <span className={exclus.includes(d.id) ? "line-through text-cfhtb-text/50" : "text-cfhtb-heading"}>
                        {d.nom || "—"} <span className="text-cfhtb-text/70">({d.email})</span>
                      </span>
                    </label>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <div className="bg-white rounded-lg border border-cfhtb-light/30 shadow-sm p-6 space-y-4" data-testid="campaign-editor">
          <h2 className="font-serif text-lg font-semibold text-cfhtb-heading">Message</h2>
          <div>
            <label className="block text-sm font-medium text-cfhtb-heading mb-1.5">Objet de l'email *</label>
            <input data-testid="campaign-objet-input" className={inputCls} value={objet} onChange={(e) => setObjet(e.target.value)} placeholder="Ex. Assemblée générale 2026 — convocation" />
          </div>
          <div>
            <label className="block text-sm font-medium text-cfhtb-heading mb-1.5">Contenu</label>
            <RichTextEditor testid="campaign-wysiwyg" variables={VARIABLES} minHeight={200}
              content="<p>{{civilite}} {{prenom}} {{nom}},</p><p></p>" onUpdate={setContenu} />
          </div>
          <div className="flex flex-wrap items-end gap-3 pt-1">
            <div className="min-w-[200px]">
              <label className="block text-xs font-medium text-cfhtb-heading mb-1">Signature</label>
              <select data-testid="campaign-signature-select" value={sigId} onChange={(e) => setSigId(e.target.value)} className={inputCls}>
                {signatures.length === 0 && <option value="defaut">Signature par défaut</option>}
                {signatures.map((s) => (
                  <option key={s.id} value={s.id}>{s.nom}{s.defaut ? " (par défaut)" : ""}</option>
                ))}
              </select>
            </div>
            <div className="min-w-[240px]">
              <label className="block text-xs font-medium text-cfhtb-heading mb-1">Adresse du test</label>
              <input type="email" data-testid="campaign-test-email-input" value={testEmail}
                onChange={(e) => setTestEmail(e.target.value)} placeholder="vous@exemple.org" className={inputCls} />
            </div>
            <button data-testid="campaign-test-button" onClick={() => sendCampaign(true)} disabled={campBusy || !config?.cle_configuree || !testEmail.includes("@")}
              className="inline-flex items-center gap-2 bg-white text-cfhtb-secondary border border-cfhtb-secondary rounded-md px-4 py-2 text-sm font-medium hover:bg-cfhtb-bg disabled:opacity-50 transition-colors">
              <Send className="h-4 w-4" /> {campBusy ? "Envoi…" : "Envoyer un test"}
            </button>
            <ApercuEmail contenu={contenu} mode="campagne" testid="campaign-apercu" signatureId={sigId === "defaut" ? null : sigId} />
            <button data-testid="campaign-send-button" onClick={() => sendCampaign(false)} disabled={campBusy || !preview || nbRetenus === 0 || !config?.cle_configuree}
              className="inline-flex items-center gap-2 bg-cfhtb-primary text-white rounded-md px-5 py-2 text-sm font-medium hover:bg-[#1f1975] disabled:opacity-50 transition-colors">
              <Mail className="h-4 w-4" /> Envoyer la campagne{preview ? ` (${nbRetenus})` : ""}
            </button>
            {!preview && <p className="text-xs text-cfhtb-text/70">Prévisualisez le segment avant l'envoi.</p>}
          </div>
          {campResult && (
            <div data-testid={campResult.envoye || campResult.mode === "reel" ? "campaign-result-success" : "campaign-result-error"}
              className={`flex items-start gap-3 rounded-md border p-4 text-sm ${
                (campResult.envoye || campResult.mode === "reel") && !campResult.detail
                  ? "bg-cfhtb-success/5 border-cfhtb-success/30 text-cfhtb-success"
                  : "bg-cfhtb-error/5 border-cfhtb-error/30 text-cfhtb-error"}`}>
              {(campResult.envoye || campResult.mode === "reel") && !campResult.detail
                ? <CheckCircle2 className="h-5 w-5 shrink-0" /> : <XCircle className="h-5 w-5 shrink-0" />}
              <div>
                {campResult.mode === "test" ? (
                  <p className="font-medium">{campResult.envoye ? `Email de test envoyé à ${campResult.destinataire}` : `Échec : ${campResult.detail}`}</p>
                ) : (
                  <p className="font-medium">Campagne envoyée : {campResult.envoyes} succès, {campResult.erreurs} erreur(s) sur {campResult.destinataires}</p>
                )}
              </div>
            </div>
          )}
        </div>

        {campagnes.length > 0 && (
          <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden" data-testid="campaign-history">
            <h2 className="font-serif text-lg font-semibold text-cfhtb-heading px-5 py-4 border-b border-slate-100">Historique des envois</h2>
            <ul className="divide-y divide-slate-100">
              {campagnes.map((c) => (
                <li key={c.id} className="px-5 py-3 flex flex-wrap items-center justify-between gap-3 text-sm">
                  <div>
                    <p className="font-medium text-cfhtb-heading">{c.objet}</p>
                    <p className="text-xs text-cfhtb-text">
                      {new Date(c.envoye_le).toLocaleString("fr-FR")} · par {c.envoye_par_email || "—"}
                    </p>
                  </div>
                  <span className="text-xs text-cfhtb-text">
                    {c.nb_envoyes}/{c.nb_destinataires} envoyés{c.nb_erreurs ? ` · ${c.nb_erreurs} erreur(s)` : ""}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </AppLayout>
  );
}
